"use client";
import { useLoginModal } from "@/utils/useLoginModal";
import { useMemo, useState } from "react";
import { RollingFund, RollingReturnResponse, TableRollingReturns } from "@/type/rollingPageTypes";
import RollingNavTable from "../others/RollingNavTable";
import RollingInsightsTable from "../others/RollingInsightsTable";
import DownloadPDFRollingTable from "../ui/DownloadPDFRollingTable";
import Link from "next/link";
import { buildTableData } from "@/utils/rollingReturnsCalculation";

export default function RollingTableSection({
  apiResult,
  mappedFunds,
  benchmarkName,
}: {
  apiResult: RollingReturnResponse[];
  mappedFunds: RollingFund[];
  benchmarkName?: string;
}) {
  const { isLoggedIn } = useLoginModal();
  const [tabularView, setTabularView] = useState(true);

  function buildVsUrlString(tableData: TableRollingReturns[]): string {
    return tableData
      .filter((item) => item.isFundData && item.url.trim() !== "")
      .map((item) => item.url)
      .join("-vs-");
  }

  const data = useMemo(() => {
    if (!apiResult.length) {
      return;
    }

    return buildTableData(apiResult, mappedFunds, benchmarkName);
  }, [apiResult, mappedFunds, benchmarkName]);

  if (!data || data.length === 0) return;

  const compareURL = `/mutual-fund/compare/${buildVsUrlString(data)}`;

  return (
    <>
      <DownloadPDFRollingTable tableData={data} tabularView={tabularView} />

      {/* Keep both mounted, just hide/show */}
      <div style={{ display: tabularView ? "block" : "none" }}>
        <RollingInsightsTable tableData={data} />
      </div>

      <div style={{ display: tabularView ? "none" : "block" }}>
        <RollingNavTable tableData={data} showTable={!tabularView} />
      </div>

      <div className="flex items-center justify-center px-3 gap-3 sm:gap-5 text-center">
        {isLoggedIn && (
          <button
            onClick={() => setTabularView((prev) => !prev)}
            className="bg-primaryGreen text-white border-2 block px-3 py-2 xs:px-10 xs:py-3 text-sm sm:text-base border-primaryGreen rounded-lg sm:rounded-3xl mt-7 transition duration-300 hover:text-primaryGreen hover:bg-transparent"
          >
            {tabularView ? "Switch to Rolling View" : "Switch to Tabular View"}
          </button>
        )}
        {data.length > 2 && (
          <Link
            href={compareURL}
            className="bg-transparent border-2 block px-3 py-2 xs:px-10 xs:py-3 text-sm sm:text-base border-primaryGreen rounded-lg sm:rounded-3xl mt-7 text-primaryGreen transition duration-300 hover:bg-primaryGreen hover:text-white"
          >
            Compare these Funds
          </Link>
        )}
      </div>
    </>
  );
}
